import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Carrot, Clock, MapPin, Settings, LogOut, Store, Megaphone, Scissors, Printer, Route, BarChart3 } from "lucide-react";
import { adminApi } from "@/lib/api";

const NAV = [
  ["/admin", "Dashboard", LayoutDashboard, true],
  ["/admin/orders", "Orders", ClipboardList],
  ["/admin/prep", "Prep list", Scissors],
  ["/admin/slips", "Packing slips", Printer],
  ["/admin/routes", "Route sheets", Route],
  ["/admin/sales", "Sales report", BarChart3],
  ["/admin/products", "Products & prices", Carrot],
  ["/admin/slots", "Delivery slots", Clock],
  ["/admin/areas", "Pincodes", MapPin],
  ["/admin/banners", "Banners", Megaphone],
  ["/admin/settings", "Settings", Settings],
];

export default function AdminLayout() {
  const location = useLocation();
  const nav = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [authed, setAuthed] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (authed) return;
    adminApi.get("/auth/me").then((r) => { setUser(r.data); setAuthed(true); })
      .catch((e) => nav("/admin/login", { replace: true, state: e?.response?.status === 403 ? { error: "This account is not an admin." } : undefined }));
  }, [authed, nav]);

  const logout = async () => { await adminApi.post("/auth/logout"); nav("/admin/login", { replace: true }); };

  if (!authed) return <div className="min-h-screen bg-[#F4F5F5] p-8 text-stone-500" data-testid="admin-loading">Checking access…</div>;

  return (
    <div className="min-h-screen bg-[#F4F5F5] md:flex">
      <aside className="md:w-60 md:min-h-screen bg-primary text-white md:fixed md:inset-y-0 flex md:flex-col print:hidden" data-testid="admin-sidebar">
        <div className="hidden md:flex items-center gap-3 px-5 py-6">
          <img src="/images/logo.jpg" alt="" className="w-10 h-10 rounded-full object-cover" />
          <div>
          <p className="font-display text-2xl font-semibold leading-none">Mukti Fresh</p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-bold">Admin</p>
          </div>
        </div>
        <nav className="flex md:flex-col overflow-x-auto no-scrollbar flex-1 md:px-3 gap-1 p-2 md:p-0">
          {NAV.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => `shrink-0 flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-white/15" : "text-white/75 hover:bg-white/10"}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:block p-3 border-t border-white/10 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-white/75 hover:bg-white/10 rounded-md" data-testid="admin-view-store"><Store className="w-4 h-4" strokeWidth={1.5} /> View store</a>
          <p className="px-3 text-xs text-white/50 truncate" data-testid="admin-user-email">{user?.email}</p>
          <button onClick={logout} className="flex items-center gap-2 px-3 py-2 text-sm text-white/75 hover:bg-white/10 rounded-md w-full" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out</button>
        </div>
      </aside>
      <main className="md:ml-60 print:ml-0 flex-1 p-4 md:p-6 min-w-0">
        <Outlet context={{ user }} />
      </main>
    </div>
  );
}
